import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RegisterDto } from '@monorepo/shared';
import { useRegister } from '../services/auth.service';
import { CardContainer } from '../components/ui/CardContainer';
import { FormInput } from '../components/ui/FormInput';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { PasswordStrengthIndicator } from '../components/ui/PasswordStrengthIndicator';
import { EMAIL_REGEX, PASSWORD_REGEX, PASSWORD_MIN_LENGTH } from '../constants/validation';
import { AuthLayout } from '../components/layout/AuthLayout';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterDto & { confirmPassword: string }>();

  const registerMutation = useRegister();
  const password = watch('password');

  const onSubmit = async (data: RegisterDto & { confirmPassword: string }) => {
    try {
      const { confirmPassword, ...registerData } = data;
      await registerMutation.mutateAsync(registerData);
      navigate('/verify-email');
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  return (
    <AuthLayout>
      <CardContainer>
        <h1 className="text-3xl font-display text-primary mb-2 text-center">
          {t('auth.signup.title')}
        </h1>
        <p className="text-secondary text-center mb-8">{t('auth.signup.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <FormInput
            label={t('auth.signup.first_name.label')}
            type="text"
            placeholder={t('auth.signup.first_name.placeholder')}
            error={errors.firstName?.message}
            required
            {...register('firstName', {
              required: t('auth.signup.first_name.error.required'),
            })}
          />

          <FormInput
            label={t('auth.signup.last_name.label')}
            type="text"
            placeholder={t('auth.signup.last_name.placeholder')}
            error={errors.lastName?.message}
            required
            {...register('lastName', {
              required: t('auth.signup.last_name.error.required'),
            })}
          />

          <FormInput
            label={t('auth.signup.email.label')}
            type="email"
            placeholder={t('auth.signup.email.placeholder')}
            error={errors.email?.message}
            required
            {...register('email', {
              required: t('auth.signup.email.error.required'),
              pattern: {
                value: EMAIL_REGEX,
                message: t('auth.signup.email.error.invalid'),
              },
            })}
          />

          <div>
            <FormInput
              label={t('auth.signup.password.label')}
              type="password"
              placeholder={t('auth.signup.password.placeholder')}
              error={errors.password?.message}
              required
              {...register('password', {
                required: t('auth.signup.password.error.required'),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: t('auth.signup.password.error.min_length'),
                },
                pattern: {
                  value: PASSWORD_REGEX,
                  message: t('auth.signup.password.error.too_weak'),
                },
              })}
            />
            <PasswordStrengthIndicator password={password || ''} />
          </div>

          <FormInput
            label={t('auth.signup.confirm_password.label')}
            type="password"
            placeholder={t('auth.signup.confirm_password.placeholder')}
            error={errors.confirmPassword?.message}
            required
            {...register('confirmPassword', {
              required: t('auth.signup.confirm_password.error.required'),
              validate: (value) =>
                value === password || t('auth.signup.confirm_password.error.mismatch'),
            })}
          />

          {registerMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">Une erreur est survenue. Veuillez réessayer.</p>
            </div>
          )}

          <PrimaryButton type="submit" loading={registerMutation.isPending}>
            {t('auth.signup.cta.submit')}
          </PrimaryButton>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-text">
            {t('auth.signup.subtitle')}{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t('auth.signup.cta.go_to_login')}
            </Link>
          </p>
        </div>
      </CardContainer>
    </AuthLayout>
  );
};
